import { actions, services } from '@ehi/global-marketing-interface';
import utils from 'utils';
import MODAL from 'constants/modals';
import FORMS from 'constants/forms';
import CUSTOM_PATH from 'constants/customPathCheckIn';
import { gmiRequest } from 'actions/request';
import { skipTheCounterStart } from 'actions/skipTheCounter';
import { profileDataSelector, authenticatedSelector } from 'selectors/profileSelectors';
import {
  getReservationProfileLoyaltyNumber,
  getReservationRenterInfoFirstName,
  getReservationRenterInfoLastName,
} from 'selectors/reservationSelectors';
import { onlineCheckInProfileSelector } from 'selectors/checkinSelectors';
import { driverInfoPhonePayloadParser } from 'utils/driverProfilePayloadParser';
import { openModalWithProps } from 'actions/modal/openModalWithProps';

const { CUSTOM_PATH_ORIGIN } = CUSTOM_PATH;

/**
 * @name checkinStart
 * @desc Dispatcher for the GMA request that kickstarts the check-in flow.
 * @param {string} origin - the page that originated the check-in start call
 * @returns {Function}
 */
export const checkinStart =
  ({ origin }) =>
  (dispatch, getState) => {
    const state = getState();
    const resLoyaltyNumber = getReservationProfileLoyaltyNumber(state);
    const { memberNumber: authenticatedUserLoyaltyNumber } = profileDataSelector(state, { memberNumber: true }) || {};

    // if this is an AI reservation...
    if (resLoyaltyNumber) {
      // ...and the user is not logged in, require login; or...
      if (!authenticatedUserLoyaltyNumber) {
        return dispatch(openModalWithProps(MODAL.SIGN_IN_MODAL, { checkinOrigin: origin }));
      }
      // ...and the logged user doesn't match the reservation driver, require logout.
      if (resLoyaltyNumber !== authenticatedUserLoyaltyNumber) {
        return dispatch(openModalWithProps(MODAL.CHECKIN_LOYALTY_CONFLICT_MODAL, { checkinOrigin: origin }));
      }
    }

    return dispatch(
      gmiRequest({
        action: actions.request({
          data: { params: { onlineCheckInOrigin: origin } },
          service: services.gma.reservations.checkinStart,
        }),
        loadingOverlay: true,
      })
    );
  };

/**
 * @name checkinAbort
 * @desc Dispatcher for the GMA request that aborts the check-in flow.
 *
 * @param {object} opts - options to customize action behavior
 * @param {boolean} opts.redirect - boolean indicating whether the GMA response redirect should be used; defaults to true.
 *
 * @returns {Function}
 */
export const checkinAbort =
  ({ redirect = true } = {}) =>
  (dispatch) =>
    dispatch(
      gmiRequest({
        action: actions.request({
          service: services.gma.reservations.checkinAbort,
        }),
        loadingOverlay: true,
        redirect,
      })
    );

/**
 * @name searchProfileByDriverLicense
 * @desc Dispatcher for the GMA request that looks up a driver profile during check-in.
 *
 * @param {object} payload
 * @param {string} payload.country
 * @param {string} payload.country_subdivision
 * @param {string} payload.driver_license_number
 * @param {string} payload.last_name
 * @param {bool}   redirect - flag to control whether request should follow the redirect path returned
 *                  //TODO - remove redirect controls once custom GMA service available for in res oci
 *
 * @returns {Function}
 */
export const searchProfileByDriverLicense =
  (payload, redirect = true) =>
  (dispatch) =>
    dispatch(
      gmiRequest({
        action: actions.request({
          service: services.gma.reservations.searchProfileByDriverLicense,
          data: { payload, params: { enableAlamoInsider: utils.config.getIsEnableAlamoInsider() } },
        }),
        loadingOverlay: true,
        redirect,
      })
    );

/**
 * @name updateProfile
 * @desc Dispatcher for the GMA request that updates a driver profile during check-in.
 *
 * @param {object} formData - Object containing all the form data from the OCI Profile step
 *  - includes Driver's License, Address and Contact Information.
 * @param {object} opt - Object with multiple options from the form.
 * @param {boolean} opt.isEditingLicenseInfo - Is user editing the profile License Info?
 * @param {boolean} opt.isEditingContactInfo - Is user editing the profile Contact Info?
 *
 * @returns {Function}
 */
export const updateProfile =
  (formData = {}, opt = {}) =>
  (dispatch, getState) => {
    const { isEditingLicenseInfo, isEditingContactInfo } = opt;
    const data = {};
    const state = getState();
    const isUserAuthenticated = authenticatedSelector(state);
    const profile = onlineCheckInProfileSelector(state);

    const isMergeProfile = isUserAuthenticated && !!profile.copy_eligible;

    // If the form contains any required data, its' an actual update, so we send profile data in the payload.
    // Else, we'll just send a skip param and go through it without making any changes.
    const formHasUpdates = !!(
      formData?.birth_date ||
      formData?.license_info?.license_number ||
      formData?.address_info?.city
    );

    if (formHasUpdates || isMergeProfile) {
      const reservationName = {
        firstName: getReservationRenterInfoFirstName(state),
        lastName: getReservationRenterInfoLastName(state),
      };
      data.payload = utils.profile.driverProfilePayloadParser(formData, profile, reservationName);

      if (isMergeProfile) {
        data.payload.copy_from_individual_id = profile.individual_id;
        // For profile merge first name should be removed from the payload otherwise GBO could
        // return error - first name do not match.
        delete data.payload.first_name;

        // For each copying info, the original (masked) values must be removed from payload.
        if (!isEditingLicenseInfo && profile.eligible_for_copy.license) {
          data.payload.copy_license_profile = true;
          delete data.payload.drivers_license;
        }
        if (!isEditingContactInfo && profile.eligible_for_copy.contact) {
          data.payload.copy_address_profile = true;
          delete data.payload.address;

          data.payload.copy_phones = true;
          delete data.payload.contact;
          delete data.payload.phones;
        }
      }
    } else {
      data.params = { skip: true };
    }

    return dispatch(
      gmiRequest({
        action: actions.request({
          service: services.gma.reservations.updateProfile,
          data,
        }),
        loadingOverlay: true,
      })
    );
  };

/**
 * @name updateProfileInRes
 * @desc Dispatcher for the GMA request that updates a driver profile during in-res-oci.
 *
 * @param {object} formData - Object containing all the form data from the OCI Profile step
 *  - includes Driver's License, Address and Contact Information.
 * @param {string} firstName - manually passing firstName while it is not available through gma online-check-in-profile
 *  TODO - remove firstName once available through gma
 *
 * @returns {Function}
 */
/* eslint-disable default-param-last */
export const updateProfileInRes =
  (formData = {}, firstName) =>
  (dispatch, getState) => {
    const data = {};

    // If the form contains any required data, its' an actual update, so we send profile data in the payload.
    // Else, we'll just send a skip param and go through it without making any changes.
    const formHasUpdates = !!(
      formData?.birth_date ||
      formData?.license_info?.license_number ||
      formData?.address_info?.city
    );

    if (formHasUpdates) {
      const state = getState();
      const profile = onlineCheckInProfileSelector(state);
      const reservationName = {
        firstName: getReservationRenterInfoFirstName(state),
        lastName: getReservationRenterInfoLastName(state),
      };
      data.payload = utils.profile.driverProfilePayloadParser(formData, profile, reservationName);
      data.payload.drivers_license.first_name = firstName;
      data.payload.first_name = firstName;

      if (
        data.payload?.phones?.length > 0 &&
        data.payload?.phones[0]?.phone_number !== formData?.driver_info?.phone_number
      ) {
        data.payload.driver_info = driverInfoPhonePayloadParser(data.payload.phones[0]);
      }

      if (
        data.payload.contact?.phones?.length > 0 &&
        !utils.fieldValidation.isMasked(data.payload.phones[0]?.phone_number)
      ) {
        Object.assign(data.payload, {
          phone_country_code: data.payload?.phones[0]?.country_code || utils.config.getDomainCountry(),
        });
      }
    } else {
      data.params = { skip: true };
    }

    return dispatch(
      gmiRequest({
        action: actions.request({
          service: services.gma.profile.updateProfileForCheckin,
          data,
        }),
        loadingOverlay: true,
      })
    );
  };
/* eslint-enable default-param-last */
/**
 * @name checkinCommit
 * @desc Dispatcher for the GMA request that commits OCI
 *
 * @param {object} formData - Check-in Review form data
 * @param {object} formData.airline_information - (optional) Airline Info data to be updated on commit
 * @param {object} formData.airline_information.code
 * @param {object} formData.airline_information.flight_number
 * @param {object} formData.partner_reward_program_code - (optional) Partner Rewards Program can be added on commit
 * @param {object} formData.partner_reward_program_member_id  - (optional) Partner Rewards Program can be added on commit
 *
 * @param {object} opts - options to customize action behavior
 * @param {boolean} opts.redirect - boolean indicating whether the GMA response redirect should be used; defaults to true.
 * @param {boolean} opts.isFromOCIFlow - boolean indicating whether it comes directly from the OCI custom path flow or not.
 *
 * @returns {Function}
 */
export const checkinCommit =
  (formData = {}, { redirect = true, isFromOCICustomPathFlow = false } = {}) =>
  (dispatch) => {
    // Due to backwards compatibility with App, we need to pass `online_check_in` as true
    // both in the service endpoint options as well as a payload property.
    const endpoint = { online_check_in: true };
    const payload = { online_check_in: true };

    if (formData?.airline_information?.code) {
      payload.airline_information = formData.airline_information;
    }

    // add partner reward program information in costum path reservation.
    // partner_reward_program_code & partner_reward_program_member_id are both required
    if (formData?.partner_reward_program_code && formData?.partner_reward_program_member_id !== FORMS.NO_SELECTION) {
      payload.partner_reward_program_code = formData.partner_reward_program_code;
      payload.partner_reward_program_member_id = formData.partner_reward_program_member_id;
    }

    return dispatch(
      gmiRequest({
        action: actions.request({
          service: services.gma.reservations.commit,
          data: { payload, endpoint },
        }),
        loadingOverlay: true,
        redirect,
        onComplete: (response) => {
          if (!response.messages) {
            // start skip the counter flow
            if (isFromOCICustomPathFlow) {
              dispatch(skipTheCounterStart({ origin: CUSTOM_PATH_ORIGIN.LOOK_UP }));
            }
          }
          return response;
        },
      })
    );
  };

/**
 * @name checkinAbort
 * @desc Dispatcher for the GMA request that aborts the check-in for profile found
 * @returns {Function}
 */
export const onlineCheckinProfileAbort = () => (dispatch) =>
  dispatch(
    gmiRequest({
      action: actions.request({
        service: services.gma.profile.abortCheckin,
      }),
      loadingOverlay: true,
    })
  );

/**
 * @name clearReservationProfile
 * @desc gmi action that clears the profile currently in GMA session
 * @returns {Function}
 */
export const clearReservationProfile = () => (dispatch) =>
  dispatch(
    gmiRequest({
      action: actions.request({
        service: services.gma.reservations.clearProfile,
        data: {
          params: { prePopulateFromRequest: true },
        },
      }),
      loadingOverlay: true,
    })
  );
